import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams();
  const comic = comics.find((c) => c.id === id);
  const [showAll, setShowAll] = useState(false);
  const [fav, setFav] = useState(false);

  const chapters = useMemo(() => Array.from({ length: comic?.chapters ?? 0 }, (_, i) => i + 1), [comic]);
  const visibleChapters = showAll ? chapters : chapters.slice(0, 10);

  const related = useMemo(
    () => (comic ? comics.filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id).slice(0, 4) : []),
    [comic],
  );

  if (!comic) {
    return (
      <>
        <Seo title="作品不存在 - 歪歪漫画" description="没有找到该作品，请返回首页继续追更。" canonicalPath="/comic/404" />
        <Navbar />
        <main className="flex min-h-[70vh] items-center justify-center pt-16">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-400" aria-hidden="true" />
            <p className="mt-4 text-foreground-700">作品走丢了，回首页找找吧。</p>
            <Link to="/" className="mt-6 inline-flex whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 hover:bg-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const crumbs = [
    { label: '首页', to: '/' },
    { label: '漫画分类', to: '/category' },
    { label: comic.category, to: `/category/${comic.categorySlug}` },
    { label: comic.title },
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: comic.title,
    author: { '@type': 'Organization', name: comic.author },
    description: comic.description,
    genre: comic.category,
    inLanguage: 'zh-CN',
    keywords: comic.tags.join(','),
    image: comic.cover,
    aggregateRating: { '@type': 'AggregateRating', ratingValue: comic.rating, bestRating: 10, ratingCount: 18200 },
  };

  return (
    <>
      <Seo
        title={`${comic.title} - 韩国歪歪漫画${comic.category}爆笑漫画`}
        description={comic.description.slice(0, 80)}
        keywords={`${comic.title},歪歪漫画,${comic.category},${comic.tags.join(',')}`}
        canonicalPath={`/comic/${comic.id}`}
        jsonLd={[articleLd]}
      />
      <Navbar />
      <main className="min-h-screen bg-background-100 pt-16 md:pt-18">
        <div className="container-page py-8 md:py-10">
          <Breadcrumb items={crumbs} />

          <div className="mt-6 grid gap-6 rounded-3xl border border-background-200/70 bg-background-50 p-5 md:p-8 lg:grid-cols-[260px_1fr]">
            <div className="overflow-hidden rounded-2xl">
              <img src={comic.cover} alt={`${comic.title}漫画封面`} className="aspect-[3/4] w-full object-cover object-top" />
            </div>
            <div className="flex flex-col">
              <div className="flex flex-wrap items-center gap-2">
                <Link to={`/category/${comic.categorySlug}`} className="rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
                  {comic.category}
                </Link>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${comic.status === '连载中' ? 'bg-secondary-100 text-secondary-900' : 'bg-accent-200 text-accent-900'}`}>
                  {comic.status}
                </span>
                <span className="flex items-center gap-1 rounded-full bg-background-200/80 px-3 py-1 text-xs text-foreground-600">
                  <i className="ri-eye-line" aria-hidden="true" /> {comic.views} 阅读
                </span>
              </div>

              <h1 className="mt-4 text-3xl text-foreground-950 md:text-4xl">{comic.title}</h1>
              <p className="mt-1 text-sm text-foreground-600">
                作者：<strong className="text-foreground-800">{comic.author}</strong> · 更新于 {comic.updatedAt}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {comic.tags.map((tag) => (
                  <span key={tag} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">
                    #{tag}
                  </span>
                ))}
              </div>

              <p className="mt-5 flex-1 text-sm leading-relaxed text-foreground-700 md:text-base">{comic.description}</p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setFav((v) => !v)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-5 py-3 text-sm font-bold transition-colors ${
                    fav ? 'bg-accent-500 text-foreground-950' : 'border border-background-300 bg-background-50 text-foreground-800 hover:border-accent-400 hover:text-accent-700'
                  }`}
                  aria-pressed={fav}
                >
                  <i className={`${fav ? 'ri-heart-3-fill' : 'ri-heart-3-line'} text-lg`} aria-hidden="true" />
                  {fav ? '已收藏' : '收藏作品'}
                </button>
                <Link
                  to="/category/hot"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-circle-line text-lg" aria-hidden="true" />
                  开始阅读
                </Link>
                <div className="ml-auto flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-star-fill text-accent-600" aria-hidden="true" />
                  <strong className="text-foreground-950">{comic.rating}</strong> 分 · {comic.chapters} 话 · {comic.favorites} 收藏
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 grid gap-8 lg:grid-cols-4">
            <div className="rounded-3xl border border-background-200/70 bg-background-50 p-5 md:p-6 lg:col-span-3">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950">
                  <i className="ri-menu-2-line text-primary-500" aria-hidden="true" />
                  章节列表
                </h2>
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  className="whitespace-nowrap rounded-full border border-background-300 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  {showAll ? '收起' : '展开全部'}
                </button>
              </div>
              <ul className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                {visibleChapters.map((n) => (
                  <li key={n}>
                    <Link to="/category/hot" className="flex items-center gap-2 rounded-lg bg-background-100 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700">
                      <i className="ri-file-text-line text-primary-500" aria-hidden="true" />
                      <span className="truncate">第 {n} 话</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="rounded-3xl border border-background-200/70 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950">
                <i className="ri-magic-line text-primary-500" aria-hidden="true" />
                作品信息
              </h2>
              <dl className="mt-4 flex flex-col gap-3 text-sm">
                <div className="flex justify-between"><dt className="text-foreground-600">作者</dt><dd className="font-bold text-foreground-950">{comic.author}</dd></div>
                <div className="flex justify-between"><dt className="text-foreground-600">分类</dt><dd className="text-primary-700">{comic.category}</dd></div>
                <div className="flex justify-between"><dt className="text-foreground-600">状态</dt><dd className="text-foreground-950">{comic.status}</dd></div>
                <div className="flex justify-between"><dt className="text-foreground-600">总话数</dt><dd className="text-foreground-950">{comic.chapters} 话</dd></div>
                <div className="flex justify-between"><dt className="text-foreground-600">评分</dt><dd className="flex items-center gap-1 text-foreground-950"><i className="ri-star-fill text-accent-600" aria-hidden="true" />{comic.rating}</dd></div>
                <div className="flex justify-between"><dt className="text-foreground-600">收藏</dt><dd className="text-foreground-950">{comic.favorites}</dd></div>
              </dl>
            </aside>
          </div>

          {related.length > 0 && (
            <section className="mt-10">
              <div className="mb-6 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950">
                  <i className="ri-refresh-line text-primary-500" aria-hidden="true" />
                  同类好作品
                </h2>
                <Link to={`/category/${comic.categorySlug}`} className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-bold text-primary-600 hover:text-primary-700">
                  查看更多 <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                {related.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}